import React from 'react';
import { useNavigate } from 'react-router';
import { Eye, Edit2, Trash2, Printer, Package, Box, MapPin, Layers, Truck } from 'lucide-react';
import { List, useDynamicRowHeight, type RowComponentProps } from 'react-window';
import { Order } from '@/types';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { loadingPlansApi } from '@/services/loading-plan-api';
import { TruckLoadDialog } from './TruckLoadDialog';

interface OrdersRoutesViewProps {
  orders: Order[];
  onEdit: (orderId: string) => void;
  onDelete: (orderId: string) => void;
  onViewDetails: (order: Order) => void;
  onPrint: (orderId: string) => void;
  statusConfig: Record<string, { label: string; color: string; icon: any }>;
  canEdit: boolean;
  canDelete: boolean;
}

type FlatItem =
  | { kind: 'header'; routeName: string; routeOrders: Order[]; routeId?: string; date: string }
  | { kind: 'order'; order: Order };

const DEFAULT_HEIGHT = 220;
const MAX_LIST_HEIGHT = 720;

const getTotalUnits = (order: Order) =>
  order.lines.reduce((acc, l) => acc + l.quantity, 0);

const formatDeliveryAddress = (order: Order): string | null => {
  if (!order.deliveryAddressLine) return null;
  const locality = [order.deliveryPostalCode, order.deliveryCity]
    .filter(Boolean).join(' ');
  return [order.deliveryAddressLine, locality].filter(Boolean).join(', ');
};

const generateRouteWhatsAppMessage = (routeName: string, routeOrders: Order[]) => {
  let message = `*Resumen de ruta: ${routeName}*\n\n`;
  routeOrders.forEach((order, index) => {
    message += `${index + 1}. *${order.customerName}*\n`;
    const addr = formatDeliveryAddress(order);
    if (addr) message += `   📍 ${addr}\n`;
    order.lines.forEach(line => {
      message += `   • ${line.productName}: ${line.quantity} ${line.unit}\n`;
    });
    message += '\n';
  });
  return encodeURIComponent(message);
};

const generateSingleOrderMessage = (order: Order) => {
  let message = `*Comanda para ${order.customerName}*\n\n`;
  const addr = formatDeliveryAddress(order);
  if (addr) message += `📍 ${addr}\n\n`;
  order.lines.forEach(line => {
    message += `• ${line.productName}: ${line.quantity} ${line.unit}\n`;
  });
  message += `\n*Total: ${getTotalUnits(order)} unidades*`;
  return encodeURIComponent(message);
};

const WHATSAPP_PATH = "M26.576 5.363c-2.69-2.69-6.406-4.354-10.511-4.354-8.209 0-14.865 6.655-14.865 14.865 0 2.732 0.737 5.291 2.022 7.491l-0.038-0.070-2.109 7.702 7.879-2.067c2.051 1.139 4.498 1.809 7.102 1.809h0.006c8.209-0.003 14.862-6.659 14.862-14.868 0-4.103-1.662-7.817-4.349-10.507l0 0zM16.062 28.228h-0.005c-0 0-0.001 0-0.001 0-2.319 0-4.489-0.64-6.342-1.753l0.056 0.031-0.451-0.267-4.675 1.227 1.247-4.559-0.294-0.467c-1.185-1.862-1.889-4.131-1.889-6.565 0-6.822 5.531-12.353 12.353-12.353s12.353 5.531 12.353 12.353c0 6.822-5.53 12.353-12.353 12.353h-0zM22.838 18.977c-0.371-0.186-2.197-1.083-2.537-1.208-0.341-0.124-0.589-0.185-0.837 0.187-0.246 0.371-0.958 1.207-1.175 1.455-0.216 0.249-0.434 0.279-0.805 0.094-1.15-0.466-2.138-1.087-2.997-1.852l0.010 0.009c-0.799-0.74-1.484-1.587-2.037-2.521l-0.028-0.052c-0.216-0.371-0.023-0.572 0.162-0.757 0.167-0.166 0.372-0.434 0.557-0.65 0.146-0.179 0.271-0.384 0.366-0.604l0.006-0.017c0.043-0.087 0.068-0.188 0.068-0.296 0-0.131-0.037-0.253-0.101-0.357l0.002 0.003c-0.094-0.186-0.836-2.014-1.145-2.758-0.302-0.724-0.609-0.625-0.836-0.637-0.216-0.010-0.464-0.012-0.712-0.012-0.395 0.010-0.746 0.188-0.988 0.463l-0.001 0.002c-0.802 0.761-1.3 1.834-1.3 3.023 0 0.026 0 0.053 0.001 0.079l-0-0.004c0.131 1.467 0.681 2.784 1.527 3.857l-0.012-0.015c1.604 2.379 3.742 4.282 6.251 5.564l0.094 0.043c0.548 0.248 1.25 0.513 1.968 0.74l0.149 0.041c0.442 0.14 0.951 0.221 1.479 0.221 0.303 0 0.601-0.027 0.889-0.078l-0.031 0.004c1.069-0.223 1.956-0.868 2.497-1.749l0.009-0.017c0.165-0.366 0.261-0.793 0.261-1.242 0-0.185-0.016-0.366-0.047-0.542l0.003 0.019c-0.092-0.155-0.34-0.247-0.712-0.434z";

type RowProps = {
  items: FlatItem[];
  statusConfig: OrdersRoutesViewProps['statusConfig'];
  canEdit: boolean;
  canDelete: boolean;
  onEdit: OrdersRoutesViewProps['onEdit'];
  onDelete: OrdersRoutesViewProps['onDelete'];
  onViewDetails: OrdersRoutesViewProps['onViewDetails'];
  onPrint: OrdersRoutesViewProps['onPrint'];
  onOpenTruckLoad: (routeId: string, routeName: string, date: string) => void;
  navigate: (path: string) => void;
};

const Row = ({
  index,
  style,
  items,
  statusConfig,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onViewDetails,
  onPrint,
  onOpenTruckLoad,
  navigate,
}: RowComponentProps<RowProps>) => {
  const item = items[index];

  if (item.kind === 'header') {
    const totalOrders = item.routeOrders.length;
    const totalUnitsInRoute = item.routeOrders.reduce((sum, o) => sum + getTotalUnits(o), 0);

    return (
      <div style={style} className="px-1">
        <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 mb-2 mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-lg font-semibold flex items-center gap-2">
            <MapPin size={20} className="text-slate-600" />
            <span>{item.routeName}</span>
          </div>

          <div className="flex items-center gap-3 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Package size={16} />
              <span className="font-semibold">{totalOrders}</span> comandas
            </span>
            <span className="flex items-center gap-1">
              <Box size={16} />
              <span className="font-semibold">{totalUnitsInRoute}</span> unidades
            </span>
          </div>

          <div className="flex gap-2 w-full md:w-auto">
            {item.routeId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex-1 md:flex-none h-9 border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 text-xs font-medium gap-1.5"
                    onClick={() => onOpenTruckLoad(item.routeId!, item.routeName, item.date)}
                  >
                    <Truck size={16} />
                    <span className="md:hidden lg:inline">Vista de carga</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cómo cargar los palets en el camión</TooltipContent>
              </Tooltip>
            )}
            {item.routeId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex-1 md:flex-none h-9 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-medium gap-1.5"
                    onClick={async () => {
                      try {
                        const plans = await loadingPlansApi.getByRoute(item.routeId!, item.date);
                        if (plans.length > 0) {
                          navigate(`/logistica/planes-carga/${plans[0].id}`);
                        } else {
                          navigate(`/logistica/planes-carga`);
                        }
                      } catch {
                        navigate(`/logistica/planes-carga`);
                      }
                    }}
                  >
                    <Layers size={16} />
                    <span className="md:hidden lg:inline">Plan de Carga</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ver plan de carga de esta ruta</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="w-full md:w-9 h-9 border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                  onClick={() => {
                    const message = generateRouteWhatsAppMessage(item.routeName, item.routeOrders);
                    window.open(`https://wa.me/34619076354?text=${message}`, '_blank');
                  }}
                >
                  <svg fill="currentColor" width="18" height="18" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <path d={WHATSAPP_PATH}></path>
                  </svg>
                  <span className="md:hidden ml-2">Enviar por WhatsApp</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Enviar resumen por WhatsApp</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }

  // kind === 'order'
  const order = item.order;
  const status = statusConfig[order.status];
  const StatusIcon = status.icon;
  const totalUnits = getTotalUnits(order);

  return (
    <div style={style} className="px-1 pb-2">
      <Card className="border-l-4 border-l-blue-400 hover:shadow-md transition-shadow">
        <CardContent className="p-3">
          <div className="flex flex-col gap-3">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-slate-900">{order.customerName}</span>
              </div>

              {order.deliveryAddressLine && (
                <div className="flex items-start gap-1.5 text-xs text-slate-600">
                  <MapPin size={12} className="mt-0.5 shrink-0 text-slate-400" />
                  <span className="leading-tight">
                    {[order.deliveryAddressLine, [order.deliveryPostalCode, order.deliveryCity].filter(Boolean).join(' ')]
                      .filter(Boolean).join(', ')}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
                  <StatusIcon size={12} />
                  {status.label}
                </span>
                <span className="flex items-center gap-1">
                  <Package size={12} />
                  {order.lines.length} productos
                </span>
                <span className="flex items-center gap-1">
                  <Box size={12} />
                  <span className="font-bold">{totalUnits} unidades</span>
                </span>
              </div>

              <div className="mt-2 pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-700 space-y-1">
                  {order.lines.map((line, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-slate-500">•</span>
                      <span className="font-medium">{line.productName}:</span>
                      <span className="font-semibold">{line.quantity} {line.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-row items-center justify-end gap-1 w-full pt-3 border-t border-slate-200">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex-1 md:flex-none md:w-8 h-12 md:h-8 justify-center bg-slate-600 md:bg-transparent hover:bg-slate-700 md:hover:bg-slate-100 text-white md:text-slate-600 p-0"
                    onClick={() => onViewDetails(order)}
                  >
                    <Eye size={20} className="md:w-4 md:h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ver detalles</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex-1 md:flex-none md:w-8 h-12 md:h-8 justify-center bg-purple-600 md:bg-transparent hover:bg-purple-700 md:hover:bg-slate-100 text-white md:text-slate-600 p-0"
                    onClick={() => onPrint(order.id)}
                  >
                    <Printer size={20} className="md:w-4 md:h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Imprimir</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex-1 md:flex-none md:w-8 h-12 md:h-8 justify-center bg-green-600 md:bg-transparent hover:bg-green-700 md:hover:bg-green-50 text-white md:text-green-600 p-0"
                    onClick={() => {
                      const message = generateSingleOrderMessage(order);
                      window.open(`https://wa.me/?text=${message}`, '_blank');
                    }}
                  >
                    <svg fill="currentColor" width="20" height="20" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="md:w-4 md:h-4">
                      <path d={WHATSAPP_PATH}></path>
                    </svg>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Enviar por WhatsApp</TooltipContent>
              </Tooltip>

              {canEdit && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex-1 md:flex-none md:w-8 h-12 md:h-8 justify-center bg-blue-600 md:bg-transparent hover:bg-blue-700 md:hover:bg-blue-50 text-white md:text-blue-600 p-0"
                      onClick={() => onEdit(order.id)}
                    >
                      <Edit2 size={20} className="md:w-4 md:h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Editar</TooltipContent>
                </Tooltip>
              )}

              {canDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex-1 md:flex-none md:w-8 h-12 md:h-8 justify-center bg-red-600 md:bg-transparent hover:bg-red-700 md:hover:bg-red-50 text-white md:text-red-600 p-0"
                      onClick={() => onDelete(order.id)}
                    >
                      <Trash2 size={20} className="md:w-4 md:h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Eliminar</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const OrdersRoutesView: React.FC<OrdersRoutesViewProps> = ({
  orders,
  onEdit,
  onDelete,
  onViewDetails,
  onPrint,
  statusConfig,
  canEdit,
  canDelete,
}) => {
  const navigate = useNavigate();
  const [truckLoadCtx, setTruckLoadCtx] = React.useState<{ routeId: string; routeName: string; date: string } | null>(null);

  const handleOpenTruckLoad = React.useCallback(
    (routeId: string, routeName: string, date: string) => {
      setTruckLoadCtx({ routeId, routeName, date });
    },
    [],
  );

  // Aplanar [{header}, ...orders] por ruta
  const flatItems = React.useMemo<FlatItem[]>(() => {
    const grouped = new Map<string, Order[]>();
    orders.forEach(order => {
      const key = order.routeName || 'Sin ruta';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(order);
    });

    const sortedEntries = Array.from(grouped.entries()).sort((a, b) => {
      if (a[0] === 'Sin ruta') return 1;
      if (b[0] === 'Sin ruta') return -1;
      return a[0].localeCompare(b[0]);
    });

    const out: FlatItem[] = [];
    sortedEntries.forEach(([routeName, routeOrders]) => {
      out.push({
        kind: 'header',
        routeName,
        routeOrders,
        routeId: routeOrders[0]?.routeId,
        date: routeOrders[0]?.orderDate,
      });
      routeOrders.forEach(order => out.push({ kind: 'order', order }));
    });
    return out;
  }, [orders]);

  // Cache key para invalidar mediciones cuando cambia el dataset
  const cacheKey = React.useMemo(
    () => `${flatItems.length}:${orders[0]?.id || ''}:${orders[orders.length - 1]?.id || ''}`,
    [flatItems.length, orders]
  );

  const dynamicRowHeight = useDynamicRowHeight({
    defaultRowHeight: DEFAULT_HEIGHT,
    key: cacheKey,
  });

  const rowProps = React.useMemo(
    () => ({
      items: flatItems,
      statusConfig,
      canEdit,
      canDelete,
      onEdit,
      onDelete,
      onViewDetails,
      onPrint,
      onOpenTruckLoad: handleOpenTruckLoad,
      navigate,
    }),
    [flatItems, statusConfig, canEdit, canDelete, onEdit, onDelete, onViewDetails, onPrint, handleOpenTruckLoad, navigate]
  );

  if (flatItems.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">No hay comandas para mostrar</div>
    );
  }

  const estimatedHeight = Math.min(MAX_LIST_HEIGHT, flatItems.length * DEFAULT_HEIGHT);

  return (
    <div className="p-4">
      <List
        rowCount={flatItems.length}
        rowHeight={dynamicRowHeight}
        defaultHeight={estimatedHeight}
        rowComponent={Row}
        rowProps={rowProps}
        overscanCount={3}
        style={{ height: estimatedHeight }}
      />
      {truckLoadCtx && (
        <TruckLoadDialog
          isOpen={!!truckLoadCtx}
          onClose={() => setTruckLoadCtx(null)}
          routeId={truckLoadCtx.routeId}
          routeName={truckLoadCtx.routeName}
          date={truckLoadCtx.date}
        />
      )}
    </div>
  );
};
